import React, { useEffect, useState, useCallback } from "react";
import { ENDPOINTS, authFetch } from "../../api/api";
import "../components/AdminLayout.css";
import { DeleteConfirm } from "./AdminServices";

const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);
  return { toast, show };
};

const TechModal = ({ tech, onClose, onSaved }) => {
  const { toast, show } = useToast();
  const isEdit = !!tech?.id;

  const [form, setForm] = useState({
    name:     tech?.name     || "",
    category: tech?.category || "frontend",
    icon:     tech?.icon     || "fa-solid fa-code",
    level:    tech?.level    || 80,
    color:    tech?.color    || "#61DAFB",
    desc:     tech?.desc     || "",
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { show("Name is required", "error"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await authFetch(`${ENDPOINTS.technologies}/${tech.id}`, {
          method: "PATCH",
          body: JSON.stringify({ ...form, level: Number(form.level) }),
        });
      } else {
        await authFetch(ENDPOINTS.technologies, {
          method: "POST",
          body: JSON.stringify({ ...form, level: Number(form.level) }),
        });
      }
      show(isEdit ? "Technology updated!" : "Technology added!");
      setTimeout(() => { onSaved(); onClose(); }, 800);
    } catch (err) {
      show(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="adm-modal-backdrop" onClick={onClose}>
        <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
          <div className="adm-modal-header">
            <h3 className="adm-modal-title">{isEdit ? "Edit Technology" : "Add Technology"}</h3>
            <button className="adm-modal-close" onClick={onClose}><i className="fa-solid fa-xmark"></i></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="adm-modal-body">
              <div className="adm-form">

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Technology Name *</label>
                    <input name="name" className="adm-form-input" value={form.name}
                      onChange={handleChange} placeholder="React" required />
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Category</label>
                    <select name="category" className="adm-form-select" value={form.category} onChange={handleChange}>
                      {["frontend","backend","database","devops","ai","tools"].map((c) => (
                        <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="adm-form-row">
                  <div className="adm-form-field">
                    <label className="adm-form-label">Icon (FontAwesome class)</label>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <input name="icon" className="adm-form-input" value={form.icon}
                        onChange={handleChange} placeholder="fa-brands fa-react" style={{ flex: 1 }} />
                      <div style={{
                        width: 38, height: 38, borderRadius: 8, flexShrink: 0,
                        background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: form.color, fontSize: "1.1rem",
                      }}>
                        <i className={form.icon}></i>
                      </div>
                    </div>
                  </div>
                  <div className="adm-form-field">
                    <label className="adm-form-label">Brand Color</label>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <input name="color" className="adm-form-input" value={form.color}
                        onChange={handleChange} placeholder="#61DAFB" style={{ flex: 1 }} />
                      <input type="color" value={form.color}
                        onChange={(e) => setForm((p) => ({ ...p, color: e.target.value }))}
                        style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "none", cursor: "pointer", padding: 2 }} />
                    </div>
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">
                    Proficiency Level: <strong style={{ color: form.color }}>{form.level}%</strong>
                  </label>
                  <input type="range" name="level" min="0" max="100" value={form.level}
                    onChange={handleChange}
                    style={{ width: "100%", accentColor: form.color || "#0a6b5a", cursor: "pointer" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", marginTop: "0.25rem" }}>
                    <span>Beginner</span><span>Intermediate</span><span>Expert</span>
                  </div>
                </div>

                <div className="adm-form-field">
                  <label className="adm-form-label">Short Description</label>
                  <textarea name="desc" className="adm-form-textarea" value={form.desc}
                    onChange={handleChange} placeholder="One line about how you use this technology" style={{ minHeight: 70 }} />
                </div>

              </div>
            </div>
            <div className="adm-modal-footer">
              <button type="button" className="adm-cancel-btn" onClick={onClose}>Cancel</button>
              <button type="submit" className="adm-save-btn" disabled={saving}>
                {saving ? "Saving..." : <><i className="fa-solid fa-check"></i> {isEdit ? "Update" : "Add"}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </>
  );
};

const AdminTech = () => {
  const [items, setItems]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [filter, setFilter]   = useState("all");
  const { toast, show }       = useToast();

  const load = useCallback(async () => {
    try {
      const data = await authFetch(ENDPOINTS.technologies);
      setItems(data);
    } catch (err) {
      show(err.message || "Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [show]);

  useEffect(() => { load(); }, [load]);

  const categories = ["all", ...new Set(items.map((t) => t.category).filter(Boolean))];
  const filtered = filter === "all" ? items : items.filter((t) => t.category === filter);

  return (
    <div>
      <div className="adm-page-header">
        <div>
          <h1 className="adm-page-title">Tech Stack</h1>
          <p className="adm-page-sub">{items.length} technologies</p>
        </div>
        <button className="adm-add-btn" onClick={() => setModal({ type: "add" })}>
          <i className="fa-solid fa-plus"></i> Add Technology
        </button>
      </div>

      {/* Category filter tabs */}
      {!loading && items.length > 0 && (
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          {categories.map((cat) => (
            <button key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: "0.35rem 0.875rem", borderRadius: 999, fontSize: "0.8rem", fontWeight: 500,
                cursor: "pointer", border: "1px solid",
                background: filter === cat ? "rgba(10,107,90,0.2)" : "transparent",
                borderColor: filter === cat ? "rgba(10,107,90,0.5)" : "rgba(255,255,255,0.1)",
                color: filter === cat ? "#0a9b7c" : "rgba(255,255,255,0.5)",
                transition: "all 0.2s",
              }}>
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div className="adm-table-wrap">
        {loading ? (
          <div className="adm-loading"><div className="adm-loading-spinner"></div></div>
        ) : filtered.length === 0 ? (
          <div className="adm-empty">No technologies yet.</div>
        ) : (
          <table className="adm-table">
            <thead>
              <tr><th>Technology</th><th>Category</th><th>Level</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                        background: `${t.color}20`, border: `1px solid ${t.color}40`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: t.color, fontSize: "0.95rem",
                      }}>
                        <i className={t.icon || "fa-solid fa-code"}></i>
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{t.name}</div>
                        {t.desc && <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)" }}>{t.desc.substring(0, 50)}{t.desc.length > 50 ? "..." : ""}</div>}
                      </div>
                    </div>
                  </td>
                  <td><span className="adm-badge adm-badge--green">{t.category || "—"}</span></td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 140 }}>
                      <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
                        <div style={{ width: `${t.level || 0}%`, height: "100%", background: t.color || "#0a9b7c", borderRadius: 3, transition: "width 0.3s" }} />
                      </div>
                      <span style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>{t.level || 0}%</span>
                    </div>
                  </td>
                  <td>
                    <div className="adm-action-btns">
                      <button className="adm-edit-btn" onClick={() => setModal({ type: "edit", item: t })}>
                        <i className="fa-solid fa-pen"></i> Edit
                      </button>
                      <button className="adm-delete-btn" onClick={() => setModal({ type: "delete", item: t })}>
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal?.type === "add" && <TechModal tech={null} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "edit" && <TechModal tech={modal.item} onClose={() => setModal(null)} onSaved={load} />}
      {modal?.type === "delete" && (
        <DeleteConfirm item={modal.item} label="Technology" endpoint={ENDPOINTS.technologies}
          onClose={() => setModal(null)} onDeleted={() => { load(); show("Technology removed."); }} />
      )}
      {toast && (
        <div className={`adm-toast adm-toast--${toast.type}`}>
          <i className={`fa-solid ${toast.type === "success" ? "fa-check" : "fa-xmark"}`}></i>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default AdminTech;
